/**
 * Ring 2 Summary Hook (Phase 25.5)
 *
 * TanStack Query hooks for the manager-facing "This week on your shifts"
 * widget. Surfaces Ring 2 fanout activity aggregated by fanoutGroupId
 * (one row per Section × Shift × Zone × variant rollout).
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// QUERY KEYS
// ============================================

export const ring2SummaryKeys = {
  all: ['ring-2-summary'] as const,
  list: (range: Ring2Range) => [...ring2SummaryKeys.all, range] as const,
};

// ============================================
// TYPES
// ============================================

export type Ring2Range = 'this-week' | 'last-week' | 'last-30-days';

export type Ring2Variant = 'refresher' | 'focused_drill';

export interface Ring2Peer {
  assignmentId: string;
  operatorId: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  status: string; // 'assigned' | 'in_progress' | 'completed' | 'overdue'
  progress: number;
  assignedAt: string;
  completedAt: string | null;
}

export interface Ring2Fanout {
  fanoutGroupId: string;
  sectionId: string | null;
  sectionTitle: string | null;
  sopId: string | null;
  sopTitle: string | null;
  materialTitle: string | null;
  shiftId: string | null;
  shiftName: string | null;
  stationId: string | null;
  stationName: string | null; // UI: "Zone" (DB: stations)
  variant: Ring2Variant;
  firedAt: string;
  assignedCount: number;
  completedCount: number;
  inProgressCount: number;
  sourceOperatorId: string | null;
  sourceOperatorName: string | null;
  sourceOperatorEmployeeId: string | null;
  ring1Status: string | null;
  peers: Ring2Peer[];
}

export interface Ring2SummaryResponse {
  weekOf: string;
  range: Ring2Range | 'custom';
  windowStart: string;
  windowEnd: string;
  fanouts: Ring2Fanout[];
}

// ============================================
// HOOKS
// ============================================

export function useRing2Summary(range: Ring2Range = 'this-week') {
  const api = useApi();

  return useQuery({
    queryKey: ring2SummaryKeys.list(range),
    queryFn: () =>
      api.get<Ring2SummaryResponse>(
        `/api/analytics/ring-2-summary?range=${encodeURIComponent(range)}`
      ),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}
